import * as React from 'react';

import type { KbMessageDto, KbProfile } from '@/api/kb-client';
import { useKbThreadDetailQuery } from '@/api/use-kb';
import { Badge } from '@/components/ui/badge';
import {
  formatDateTime,
  senderRoleLabel,
} from '@/routes/communication/chat-format';
import { archiveReasonLabel } from '@/routes/communication/kb-format';
import { cn } from '@/lib/utils';

// =============================================================================
// kb-thread-detail.tsx — Panneau droit : vue détail LECTURE SEULE d'un fil archivé (R15-COM-04).
// =============================================================================
// §2.7.4 / §3.9 : consultation STRICTEMENT en lecture seule — méta (SIRET, campagne, motif d'archivage,
// période, nb messages), participants, et fil chronologique. AUCUN champ de composition, AUCUNE
// réouverture, AUCUNE action d'écriture ici (le tagging admin vit dans un panneau séparé). Le `content`
// est du TEXTE BRUT rendu JSX ÉCHAPPÉ (`{message.content}`), JAMAIS `dangerouslySetInnerHTML`.

export interface KbThreadDetailProps {
  readonly profile: KbProfile;
  readonly threadId: string;
  /**
   * Masque l'en-tête méta interne. Optionnel (défaut `false` → comportement inchangé pour /archives
   * admin). Permet à un écran hôte de porter SON PROPRE en-tête unifié au-dessus (pas de double bandeau).
   */
  readonly hideHeader?: boolean;
}

export function KbThreadDetail({
  profile,
  threadId,
  hideHeader = false,
}: KbThreadDetailProps): React.JSX.Element {
  const detailQuery = useKbThreadDetailQuery(profile, threadId);

  if (detailQuery.isLoading) {
    return (
      <p role="status" className="p-6 text-sm text-muted-foreground">
        Chargement de la conversation archivée…
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data === undefined) {
    return (
      <p role="alert" className="p-6 text-sm text-destructive">
        {detailQuery.error?.message ??
          'La conversation archivée est momentanément indisponible.'}
      </p>
    );
  }

  const detail = detailQuery.data;

  return (
    <section
      aria-label="Conversation archivée"
      className="flex h-full min-h-0 flex-col"
    >
      {hideHeader ? null : (
      <header className="border-b border-border px-6 py-4">
        <h2 className="font-serif text-base font-semibold">
          {profile === 'admin'
            ? `Fournisseur ${detail.supplierSiret}`
            : 'Conversation archivée avec JeKo'}
        </h2>
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <div>
            <dt className="inline font-medium text-foreground">Campagne : </dt>
            <dd className="inline">{detail.campaignId}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-foreground">
              Motif d'archivage :{' '}
            </dt>
            <dd className="inline">{archiveReasonLabel(detail.archivedReason)}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-foreground">Archivé le : </dt>
            <dd className="inline">{formatDateTime(detail.archivedAt)}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-foreground">Messages : </dt>
            <dd className="inline">{detail.messageCount}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-foreground">Période : </dt>
            <dd className="inline">
              {formatDateTime(detail.periodFrom)} → {formatDateTime(detail.periodTo)}
            </dd>
          </div>
        </dl>
        {detail.participants.length > 0 ? (
          <div className="mt-2">
            <p className="text-xs font-medium text-foreground">Participants</p>
            <ul className="mt-1 flex flex-wrap gap-1">
              {detail.participants.map((participant) => (
                <li key={participant.senderRef}>
                  <Badge variant="secondary">
                    {senderRoleLabel(
                      participant.senderType === 'Admin' ? 'Admin' : 'Master',
                    )}{' '}
                    · {participant.messageCount} msg
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {detail.tags.length > 0 ? (
          <div className="mt-2">
            <p className="text-xs font-medium text-foreground">Tags</p>
            <ul className="mt-1 flex flex-wrap gap-1">
              {detail.tags.map((tag) => (
                <li key={tag.tagId}>
                  <Badge variant="outline">{tag.label}</Badge>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </header>
      )}

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {detail.messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Cette conversation archivée ne contient aucun message.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {detail.messages.map((message) => (
              <KbMessageBubble key={message.messageId} message={message} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Une bulle de message archivé (LECTURE SEULE). `senderType==='Admin'` aligné à droite (côté JeKo). */
function KbMessageBubble({
  message,
}: {
  readonly message: KbMessageDto;
}): React.JSX.Element {
  const fromAdmin = message.senderType === 'Admin';
  return (
    <li className={cn('flex flex-col', fromAdmin ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-lg border px-3 py-2 text-sm',
          fromAdmin
            ? 'border-primary/30 bg-primary/10 text-foreground'
            : 'border-border bg-card text-card-foreground',
        )}
      >
        {/* TEXTE BRUT rendu ÉCHAPPÉ (anti-XSS) : jamais dangerouslySetInnerHTML. */}
        <p className="whitespace-pre-wrap break-words">{message.content}</p>
      </div>
      <p className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span>{senderRoleLabel(message.senderRoleAtSend)}</span>
        <span aria-hidden="true">·</span>
        <span>{formatDateTime(message.sentAt)}</span>
      </p>
    </li>
  );
}
